import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { useCookies } from 'react-cookie'

import { PrintProvider } from './context/printContext'

import { Alert, Box, Container, Grow } from '@mui/material'

import { SideMenu } from 'components/sideMenu'
import { useGetNotificationUnreadCount } from 'interface/notification'
import { NotificationDialog } from 'features/notification/dialog/list'
import { TopBar } from 'components/topBar'

import { SearchListPage } from 'features/search/list'
import { SearchHomePage } from 'features/search/home'
import { ConfigurePage } from 'features/configure'
import { DpcResultsPage } from 'features/dpcResult'
import { MatchingPage } from 'features/matching'
import { KojinAnalyticsPage } from 'features/kojin/analytics'
import { SisatuAnalyticsPage } from 'features/sisetu/analytics'
import { GuideLineListPage } from 'features/guideLine/list'
import { GuideLinePage } from 'features/guideLine/home'
import { ConventionListPage } from 'features/convention/list'
import { ConventionPage } from 'features/convention/home'
import { useSideMenu } from 'components/sideMenu/container'
import { useTopBar } from 'components/topBar/container'
import { safeParseInt } from 'shared/util'
import { GridPage } from 'features/grid'
import { AuthorPreviewPage, KojinPreviewPage } from 'features/kojin'
import { SisetuPreviewPage } from 'features/sisetu'
import { useUserSession } from 'features/session/context'
import { DoLogoff } from 'interface/login'
import { useCheckAgreement } from 'interface/agreement'
import { AgreementPreviewDialog } from 'features/agreement/dialog/preview'

type InsideRouteProps = {
	onLogoff: (keepUrl: boolean) => void
}
export const InsideRoute = ({
	onLogoff: logoff
}: InsideRouteProps) => {
	const navigate = useNavigate()
	const { loginToken } = useUserSession()
  const [cookie, setCookie] = useCookies(['notification'])
  const [printNodes, setPrintNodes] = useState<ReactNode | undefined>(undefined)
  const [modal, setModal] = useState<'notification' | 'agreement' | null>(null)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState<number>(0)

  const notificationsUnreadCountState = useGetNotificationUnreadCount()
	const checkAgreementState = useCheckAgreement()

	const isOpenedFirstNotification = useMemo(
    () => cookie.notification?.isOpenedFirstNotification ?? false,
    [cookie]
  )
  const onOpenedFirstNotification = useCallback(
    () => {
      setCookie('notification', {
        isOpenedFirstNotification: true
      })
    },
    [setCookie]
  )
  const onOpenNotification = useCallback(
    () => {
      setModal('notification')
    },
    []
  )
	const onClickAgreement = useCallback(
		() => {
			setModal('agreement')
		},
		[]
	)
	const onLogoff = useCallback(
		(keepUrl: boolean) => {
			DoLogoff(loginToken)
			logoff(keepUrl)
		},
		[logoff, loginToken]
	)
	const sideMenuState = useSideMenu({
		unreadNotificationCount,
		onLogoff,
		onOpenNotification
	})
	const topBarState = useTopBar()

  useEffect(
    () => {
      if (notificationsUnreadCountState.loading) return
      if (!notificationsUnreadCountState.data) return
      if (!isOpenedFirstNotification && notificationsUnreadCountState.data > 0) {
        onOpenNotification()
        onOpenedFirstNotification()
      }
      setUnreadNotificationCount(notificationsUnreadCountState.data)
    },
    [
			notificationsUnreadCountState, 
			isOpenedFirstNotification, onOpenedFirstNotification,
			onOpenNotification, setUnreadNotificationCount
		]
  )
	return (
		<PrintProvider doPrint={setPrintNodes}>
			<Box
				sx={{
					backgroundColor: (theme) => theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
					height: '100vh',
					maxHeight: '100vh',
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column'
				}}
			>
				<TopBar topBarState={topBarState} sideMenuState={sideMenuState} onLogoff={onLogoff}/>
				
				{checkAgreementState.data?.lastestVersion && checkAgreementState.data.lastestVersion > (checkAgreementState.data.agreedVersion ?? 0) ? (
					<Container maxWidth="xl" sx={{ paddingTop: 2 }}>
						<Grow in={true}>
							<Alert
								variant="filled"
								severity="warning"
								elevation={3}
								onClick={onClickAgreement}
								sx={{ cursor: 'pointer' }}
							>
								利用規約が更新されています。クリックして内容をご確認ください。
							</Alert>
						</Grow>
					</Container>
				) : <></>}

				<SideMenu sideMenuState={sideMenuState}/>

				<Routes>
					<Route index={true} element={<Navigate to="./search/list" replace></Navigate>}/>

					<Route path={'configure/:activePage?'} element={
						<ConfigurePage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />

					<Route path={'dpcResult'} element={
						<DpcResultsPage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />
					<Route path={'matching'} element={
						<MatchingPage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />

					<Route path={'guideLine/list'} element={
						<GuideLineListPage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />
					<Route path={'guideLine/:guideLineId'} element={
						<GuideLinePage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />

					<Route path={'convention/list'} element={
						<ConventionListPage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />
					<Route path={'convention/:conventionId'} element={
						<ConventionPage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />

					<Route path={'kojin/:kojinCode'} element={
						<KojinPreviewPage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />

					<Route path={'sisetu/:sisetuCode'} element={
						<SisetuPreviewPage topBarState={topBarState} sideMenuState={sideMenuState}/>
					} />

					<Route path={'search/list'} element={
						<SearchListPage
							topBarState={topBarState} sideMenuState={sideMenuState}
							onSelectSearch={(searchId) => navigate(`../search/${searchId}/home`)}
						/>
					} />
					<Route path={'search/:searchId/*'} element={
						<SearchRoute topBarState={topBarState} sideMenuState={sideMenuState} />
					}/>
				</Routes>
			</Box>

			{modal === "notification" ? (
				<NotificationDialog
					onClose={() => setModal(null)}
					onChangeNotificationReadedCount={(count) => setUnreadNotificationCount(count)}
				/>
			) : modal === 'agreement' ? (
				<AgreementPreviewDialog
					onAgree={() => {
						setModal(null)
						checkAgreementState.refetch()
					}}
					onClose={() => {
						setModal(null)
					}}
				/>
			) : <></>}

			{printNodes && <div id="printArea">{printNodes}</div>}
		</PrintProvider>
	)
}

type SearchRouteProps = {
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
}
const SearchRoute = ({
	topBarState,
	sideMenuState
}: SearchRouteProps) => {
	const navigate = useNavigate()
	const { searchId: searchIdStr } = useParams()
  const searchId = safeParseInt(searchIdStr) ?? null
  if (!searchId) {
		return (
			<Alert severity="error" sx={{ wordBreak: 'break-all' }}>パラメータ不正</Alert>
		)
	}
	return (
		<Routes>
			<Route index={true} element={<Navigate to="./home" replace></Navigate>}/>

			<Route path={'home'} element={
				<SearchHomePage
					flexGrow={1} 
					searchId={searchId}
					topBarState={topBarState} sideMenuState={sideMenuState}
					onDeleteSearch={() => navigate("./../../list")}
					openSisetu={() => navigate("./../sisetu/analytics")}
					openKojin={() => navigate("./../kojin/analytics")}
					openGrid={() => navigate("./../grid")}
					openTargetList={(targetListId, isOpenGrid) => navigate(isOpenGrid ? `./../grid?listId=${targetListId}` : `./../kojin/analytics?listId=${targetListId}`)}
					openMrpProject={(projectId, isOpenGrid) => navigate(isOpenGrid ? `./../grid?mrp=${projectId}` : `./../kojin/analytics?mrp=${projectId}`)}
					openLvProject={(projectCode, isOpenGrid) => navigate(isOpenGrid ? `./../grid?lv=${projectCode}` : `./../kojin/analytics?lv=${projectCode}`)}
					openGuideLine={(guideLineId) => navigate(`./../../guideLine/${guideLineId}`)}
					openConvention={(conventionId) => navigate(`./../../convention/${conventionId}`)}
				/>
			} />
			<Route path="kojin/analytics" element={
				<KojinAnalyticsPage
					flexGrow={1}
					searchId={searchId}
					topBarState={topBarState}
					sideMenuState={sideMenuState}
				/>
			} />
			<Route path={'kojin/:kojinCode'} element={
				<KojinPreviewPage
					flexGrow={1}
					searchId={searchId}
					topBarState={topBarState}
					sideMenuState={sideMenuState}
				/>
			} />
			<Route path={'author/:authorId'} element={
				<AuthorPreviewPage
					flexGrow={1}
					searchId={searchId}
					topBarState={topBarState}
					sideMenuState={sideMenuState}
				/>
			} />
			<Route path="sisetu/analytics" element={
				<SisatuAnalyticsPage
					flexGrow={1}
					searchId={searchId}
					topBarState={topBarState}
					sideMenuState={sideMenuState}
				/>
			} />
			<Route path={'sisetu/:sisetuCode'} element={
				<SisetuPreviewPage
					flexGrow={1}
					searchId={searchId}
					topBarState={topBarState}
					sideMenuState={sideMenuState}
				/>
			} />
			<Route path={'grid'} element={
				<GridPage
					flexGrow={1}
					searchId={searchId}
					topBarState={topBarState}
					sideMenuState={sideMenuState}
				/>
			}/>
		</Routes>
	)	
}